import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {AbortController | undefined} */
  #autoplayAbort;

  connectedCallback() {
    super.connectedCallback();

    this.#autoplayAbort?.abort();
    this.#autoplayAbort = new AbortController();
    const signal = this.#autoplayAbort.signal;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.load();

    this.#startAutoplay(videoElement, signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#autoplayAbort?.abort();
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {AbortSignal} signal
   */
  #startAutoplay(video, signal) {
    const tryPlay = () => {
      video.muted = true;
      video.play().catch(() => {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
    } else {
      video.addEventListener('canplay', () => tryPlay(), { once: true, signal });
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible' && video.paused) {
          tryPlay();
        }
      },
      { signal }
    );
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
